import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router";

import { Container } from "../components/Container";
import { LegalBody } from "../components/LegalBody";
import { LegalStickyToc } from "../components/LegalStickyToc";
import { SEO } from "../components/SEO";
import {
  getLegalDocument,
  hasLocalizedLegalDocument,
  LEGAL_SLUGS,
} from "../lib/legal/documents";
import type { LegalSlug } from "../lib/legal/types";
import { useLocale } from "../hooks/useLocale";

type LegalDocumentProps = {
  slug: LegalSlug;
};

export function LegalDocument({ slug }: LegalDocumentProps) {
  const { t } = useTranslation();
  const { pathFor, code, language } = useLocale();

  const doc = getLegalDocument(slug, code);
  if (!doc) {
    return <Navigate to={pathFor("/")} replace />;
  }

  const isLocalized = hasLocalizedLegalDocument(slug, code);
  const showEnglishNotice = code !== "en" && !isLocalized;
  const contentLang = isLocalized ? language.acceptLanguageCode : "en";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug, code]);

  return (
    <main className="pt-28 sm:pt-32">
      <SEO
        title={doc.meta.title}
        description={doc.meta.description}
        path={`/${slug}`}
      />

      <Container size="xl">
        <article className="py-12 sm:py-16 md:pb-16">
          <header className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {doc.eyebrow}
            </p>
            <h1 className="hero-headline text-text-heading">{doc.title}</h1>
            {doc.subtitle ? (
              <p className="mt-4 text-lg text-text-muted">{doc.subtitle}</p>
            ) : null}

            <dl className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-text-subtle">
              {doc.version ? (
                <div>
                  <dt className="sr-only">Version</dt>
                  <dd>Version {doc.version}</dd>
                </div>
              ) : null}
              {doc.lastUpdated ? (
                <div>
                  <dt className="sr-only">Last updated</dt>
                  <dd>
                    {t("legal.last_updated", { date: doc.lastUpdated })}
                  </dd>
                </div>
              ) : null}
              {doc.effectiveDate ? (
                <div>
                  <dt className="sr-only">Effective date</dt>
                  <dd>
                    {t("legal.effective_date", { date: doc.effectiveDate })}
                  </dd>
                </div>
              ) : null}
            </dl>

            {showEnglishNotice ? (
              <p
                className="mt-8 rounded-xl border border-border-soft bg-bg-subtle px-4 py-3 text-sm text-text-muted dark:bg-bg-muted"
                lang="en"
              >
                {t("legal.english_notice")}
              </p>
            ) : null}
          </header>

          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-[minmax(0,1fr)_14rem] md:gap-10 lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-12 xl:grid-cols-[minmax(0,1fr)_18rem]">
            <div
              className="min-w-0 pb-44 md:max-w-3xl md:pb-0"
              lang={contentLang}
            >
              {doc.preamble ? (
                <div className="mb-12 space-y-4">
                  <LegalBody text={doc.preamble} />
                </div>
              ) : null}

              <div className="space-y-12">
                {doc.sections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-32"
                  >
                    <h2 className="text-2xl font-bold text-text-heading sm:text-3xl">
                      {section.heading}
                    </h2>
                    {section.body ? (
                      <div className="mt-4">
                        <LegalBody text={section.body} />
                      </div>
                    ) : null}
                    {section.subsections?.map((sub) => (
                      <div
                        key={sub.id}
                        id={sub.id}
                        className="mt-8 scroll-mt-32 border-t border-border-soft/80 pt-8"
                      >
                        <h3 className="text-xl font-semibold text-text-heading sm:text-2xl">
                          {sub.heading}
                        </h3>
                        <div className="mt-4">
                          <LegalBody text={sub.body} />
                        </div>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>

            <LegalStickyToc sections={doc.sections} />
          </div>

          <aside className="mt-16 max-w-3xl border-t border-border-soft pt-10">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-text-subtle">
              {t("legal.related_documents")}
            </h2>
            <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
              {LEGAL_SLUGS.filter((s) => s !== slug).map((related) => {
                const relatedDoc = getLegalDocument(related, code);
                if (!relatedDoc) return null;
                return (
                  <li key={related}>
                    <Link
                      to={pathFor(`/${related}`)}
                      className="text-sm text-brand-500 underline-offset-2 hover:underline"
                    >
                      {relatedDoc.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </aside>
        </article>
      </Container>
    </main>
  );
}
